import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faSpinner,
  faRedo as iconRefresh,
} from '@fortawesome/free-solid-svg-icons';

import Switch from 'react-switch';

import Title from '../../../components/Title';
import { auth } from '../../../helpers/firebase';
import { toastError, toastSuccess } from '../../../helpers/toastHelpers';
import { titleHelper } from '../../../helpers/titleHelper';
import { getDraftDetailsForYear, getDraftYears } from '../../../data/getDraftDetails';
import getDraftForAll from '../../../data/getDraftForAll';
import setDraftForUser from '../../../data/setDraftForUser';
import { setDraftDetails } from '../../../data/setDraftDetails';
import '../admin.css';

const DraftCreate = () => {
  const currentYear = new Date().getFullYear().toString();
  const [draftYears, setDraftYears] = useState([]);
  const [selectedDraftYear, setSelectedDraftYear] = useState('');
  const [draftYearInput, setDraftYearInput] = useState(currentYear);
  const [selectedDraftDetails, setSelectedDraftDetails] = useState(null);
  const [selectedDraftEntries, setSelectedDraftEntries] = useState({});
  const [draftDateOverride, setDraftDateOverride] = useState('');
  const [draftInfoDueOverride, setDraftInfoDueOverride] = useState('');
  const [disabledOverride, setDisabledOverride] = useState(true);
  const [copyUserDraftData, setCopyUserDraftData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingDraftDetails, setLoadingDraftDetails] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadDraftYears = useCallback(async () => {
    setLoading(true);
    const result = await getDraftYears();

    if (result.error) {
      toastError(result.message);
      setDraftYears([]);
      setSelectedDraftYear('');
    } else {
      setDraftYears(result.draftYears);
      setSelectedDraftYear(result.draftYears[0] || '');
    }

    setLoading(false);
  }, []);

  const handleDraftYearInput = (event) => {
    setDraftYearInput(event.target.value.replace(/\D/g, ''));
  };

  const loadSelectedDraftDetails = useCallback(async () => {
    if (!selectedDraftYear) {
      setSelectedDraftDetails(null);
      setSelectedDraftEntries({});
      return;
    }

    setLoadingDraftDetails(true);
    const [detailResult, entriesResult] = await Promise.all([
      getDraftDetailsForYear(selectedDraftYear),
      getDraftForAll(selectedDraftYear),
    ]);

    if (!detailResult.success) {
      toastError(detailResult.error?.message || 'Unable to load selected draft details');
      setSelectedDraftDetails(null);
      setSelectedDraftEntries({});
      setLoadingDraftDetails(false);
      return;
    }

    if (entriesResult.error) {
      toastError(entriesResult.message || 'Unable to load selected draft entries');
      setSelectedDraftEntries({});
    } else {
      setSelectedDraftEntries(entriesResult.draft || {});
    }

    const { success, ...details } = detailResult;
    setSelectedDraftDetails(details);
    setDraftDateOverride(details.draftDate || '');
    setDraftInfoDueOverride(details.draftInfoDue || '');
    setDisabledOverride(Boolean(details.disabled));
    setLoadingDraftDetails(false);
  }, [selectedDraftYear]);

  const generateDraftYear = useCallback(async () => {
    const targetYear = draftYearInput.trim();

    if (!/^[0-9]+$/.test(targetYear)) {
      toastError('Enter a valid year using numbers only');
      return;
    }

    if (!selectedDraftYear) {
      toastError('Select a source draft year first');
      return;
    }

    if (draftYears.includes(targetYear)) {
      toastError(`Draft year ${targetYear} already exists`);
      return;
    }

    setGenerating(true);

    try {
      const sourceDraftDetails = await getDraftDetailsForYear(selectedDraftYear);

      if (!sourceDraftDetails.success) {
        toastError(sourceDraftDetails.error?.message || 'Unable to load source draft year');
        return;
      }

      const { success, ...draftDetailsData } = sourceDraftDetails;

      // Apply user overrides before writing the new year document.
      draftDetailsData.draftDate = draftDateOverride || draftDetailsData.draftDate;
      draftDetailsData.draftInfoDue = draftInfoDueOverride || draftDetailsData.draftInfoDue;
      draftDetailsData.disabled = disabledOverride;

      const topLevelResult = await setDraftDetails(draftDetailsData, targetYear);
      if (!topLevelResult.success) {
        toastError(topLevelResult.message || 'Unable to create draft year');
        return;
      }

      if (copyUserDraftData) {
        const sourceDraftEntries = await getDraftForAll(selectedDraftYear);

        if (sourceDraftEntries.error) {
          toastError(sourceDraftEntries.message || 'Unable to load source draft entries');
          return;
        }

        // Copy the per-user draft documents into the new year.
        // The top-level draft/year document is created via setDraftDetails above.
        // eslint-disable-next-line no-restricted-syntax
        for (const [userId, draftData] of Object.entries(sourceDraftEntries.draft || {})) {
          // eslint-disable-next-line no-await-in-loop
          const userResult = await setDraftForUser(userId, draftData, targetYear);
          if (!userResult.success) {
            throw new Error(userResult.message || `Unable to copy draft data for ${userId}`);
          }
        }
      }

      toastSuccess(`Draft year ${targetYear} created successfully`);
      await loadDraftYears();
      setDraftYearInput((Number(targetYear) + 1).toString());
    } catch (error) {
      toastError(error.message || 'Unable to generate draft year');
    } finally {
      setGenerating(false);
    }
  }, [
    copyUserDraftData,
    disabledOverride,
    draftDateOverride,
    draftInfoDueOverride,
    draftYearInput,
    draftYears,
    loadDraftYears,
    selectedDraftYear
  ]);

  useEffect(() => {
    titleHelper('Draft Crteator');

    if (auth.currentUser) {
      loadDraftYears();
    }
  }, [loadDraftYears]);

  useEffect(() => {
    loadSelectedDraftDetails();
  }, [loadSelectedDraftDetails]);

  return (
    <div id="psf-draft-admin-container" className="psf-container">
      <div className="container">
        <Title text="Draft Generator" />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <Link to="/draft/admin" className="btn btn-sm btn-outline-success text-green px-3 shadow">
            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
            Back To Draft Admin
          </Link>

          <button
            type="button"
            className="btn btn-sm btn-success px-3 shadow"
            onClick={loadDraftYears}
          >
            <FontAwesomeIcon icon={iconRefresh} />
          </button>
        </div>
      </div>

      <div className="container-fluid shadow row-background p-4">
        {loading ? (
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} size="3x" spin className="text-yellow" />
          </div>
        ) : (
          <div className="row">
            <div className="col-12 col-md-8 col-lg-6 mb-4">
              <label className="label text-yellow-light" htmlFor="draft-document-select">Draft Year</label>
              <select
                id="draft-document-select"
                className="form-control"
                value={selectedDraftYear}
                onChange={(event) => setSelectedDraftYear(event.target.value)}
              >
                {draftYears.length > 0 ? draftYears.map((draftYear) => (
                  <option key={draftYear} value={draftYear}>{draftYear}</option>
                )) : <option value="">No draft documents found</option>}
              </select>
            </div>

            <div className="col-12 col-md-8 col-lg-6 mb-4">
              <label className="label text-yellow-light" htmlFor="new-draft-year-input">New Draft Year</label>
              <input
                id="new-draft-year-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="form-control"
                value={draftYearInput}
                onChange={handleDraftYearInput}
                maxLength={4}
                placeholder={currentYear}
              />
              <small className="form-text text-mute text-italic mt-2">
                Only numbers are allowed. The default is the current year.
              </small>
            </div>

            <div className="col-12 col-md-6 mb-4">
              <label className="label text-yellow-light" htmlFor="draft-date-override">Draft Date</label>
              <input
                id="draft-date-override"
                type="text"
                className="form-control"
                value={draftDateOverride}
                onChange={(event) => setDraftDateOverride(event.target.value)}
                placeholder="e.g. August 24, 2027 7:00 PM"
              />
              <small className="form-text text-mute text-italic mt-1">Overrides the source year's draft date.</small>
            </div>

            <div className="col-12 col-md-6 mb-4">
              <label className="label text-yellow-light" htmlFor="draft-info-due-override">Draft Info Due</label>
              <input
                id="draft-info-due-override"
                type="text"
                className="form-control"
                value={draftInfoDueOverride}
                onChange={(event) => setDraftInfoDueOverride(event.target.value)}
                placeholder="e.g. August 20, 2027 11:59 PM"
              />
              <small className="form-text text-mute text-italic mt-1">Overrides the source year's draft info due date.</small>
            </div>

            <div className="col-12 col-md-6 mb-4">
              <label className="label text-yellow-light d-block mb-2">Draft Disabled</label>
              <div className="d-flex align-items-center">
                <Switch
                  checked={disabledOverride}
                  onChange={setDisabledOverride}
                  onColor="#668466"
                  onHandleColor="#06342D"
                  handleDiameter={30}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                  activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                  height={20}
                  width={48}
                />
                <span className="ml-3">{disabledOverride ? 'Disabled' : 'Enabled'}</span>
              </div>
              <small className="form-text text-mute text-italic mt-1">Overrides whether draft is disabled in the new year.</small>
            </div>

            <div className="col-12 col-md-6 mb-4">
              <label className="label text-yellow-light d-block mb-2">Copy User Draft Data</label>
              <div className="d-flex align-items-center">
                <Switch
                  checked={copyUserDraftData}
                  onChange={setCopyUserDraftData}
                  onColor="#668466"
                  onHandleColor="#06342D"
                  handleDiameter={30}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                  activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                  height={20}
                  width={48}
                />
                <span className="ml-3">{copyUserDraftData ? 'Will copy' : 'Will not copy'} user draft data</span>
              </div>
              <small className="form-text text-mute text-italic mt-1">When off, only the top-level draft year document is created.</small>
            </div>

            <div className="col-12 col-md-4 col-lg-3">
              <button
                type="button"
                className="btn btn-lg btn-success mt-2 py-3 btn-block shadow"
                onClick={generateDraftYear}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Generating
                  </>
                ) : 'Generate'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="container-fluid shadow row-background p-4 mt-3">
        <h4 className="mb-3">Draft Details</h4>

        {loadingDraftDetails ? (
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} size="2x" spin className="text-yellow" />
          </div>
        ) : selectedDraftDetails ? (
          <div className="row">
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Year:</span>
              <span className="ml-2">{selectedDraftYear}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Date:</span>
              <span className="ml-2">{selectedDraftDetails.draftDate || 'Not Set'}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Info Due:</span>
              <span className="ml-2">{selectedDraftDetails.draftInfoDue || 'Not Set'}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Disabled:</span>
              <span className="ml-2">{selectedDraftDetails.disabled ? 'Yes' : 'No'}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Force Draft Open:</span>
              <span className="ml-2">{selectedDraftDetails.forceOpen ? 'Yes' : 'No'}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Order Entries:</span>
              <span className="ml-2">{Array.isArray(selectedDraftDetails.draftOrder) ? selectedDraftDetails.draftOrder.length : 0}</span>
            </div>
            <div className="col-12 col-md-6 mb-2">
              <span className="text-bold text-yellow-light">Draft Links:</span>
              <span className="ml-2">{Array.isArray(selectedDraftDetails.draftLinks) ? selectedDraftDetails.draftLinks.length : 0}</span>
            </div>

            <div className="col-12 mt-3">
              <h5 className="mb-3">Draft User Data</h5>
              <p className="mb-2">
                <span className="text-bold text-yellow-light">Total Team Entries:</span>
                <span className="ml-2">{Object.keys(selectedDraftEntries).length}</span>
              </p>

              {Object.keys(selectedDraftEntries).length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th scope="col">User ID</th>
                        <th scope="col">Team Name</th>
                        <th scope="col">Songs</th>
                        <th scope="col">Logo Upload</th>
                        <th scope="col">Draft In Person</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selectedDraftEntries).map(([userId, userDraft]) => {
                        const songListCount = Array.isArray(userDraft.songList)
                          ? userDraft.songList.length
                          : (userDraft.songList ? JSON.parse(userDraft.songList).length : 0);

                        return (
                          <tr key={userId}>
                            <td>{userId}</td>
                            <td>{userDraft.teamName || 'Not Set'}</td>
                            <td>{songListCount}</td>
                            <td>{userDraft.logoUpload ? 'Yes' : 'No'}</td>
                            <td>{userDraft.draftInPerson ? 'Yes' : 'No'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mb-0 text-mute">No subcollection entries found for this year.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="mb-0 text-mute">Select a draft year to view its details.</p>
        )}
      </div>
    </div>
  );
};

export default DraftCreate;